import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { sendErrorReport } from 'shared/helpers'
import {
  DirtyFormAlert,
  Label,
  Modal,
  Notification,
  Selector,
  ContentLoader
} from 'shared/components'
import { environmentOptions } from 'shared/constants'
import { displayValue } from 'shared/helpers'
import { fetchInstallationFiles } from 'src/product/actions'
import { patchLicense } from 'src/license/actions'

const LicenseProductVersionForm = ({
  closeCb,
  productVersionID,
  licenseID,
  productID,
  companyID,
  confirmCb
}) => {
  const [dirty, setDirty] = useState(false)
  const [isDirtyFormAlertDisplayed, setDirtyFormDisplay] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState(productVersionID || '')
  const [versions, setVersions] = useState([])
  const [loadingVersions, setLoadingVersions] = useState(true)

  const getVersions = useCallback(() => {
    fetchInstallationFiles(productID, companyID)
      .then(res => {
        setVersions(get(res, 'data.results') || [])
        setLoadingVersions(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot fetch product versions')
        setLoadingVersions(false)
        Notification('error', __('Error occured'), __('We could not load the product versions.'))
      })
  }, [productID])

  useEffect(() => {
    getVersions()
  }, [getVersions])

  const handleClose = () => {
    if (!dirty) {
      return closeCb()
    }
    return setDirtyFormDisplay(true)
  }

  const updateLicenseProductVersion = () => {
    const data = {
      installation_file: selectedVersion
    }
    patchLicense(licenseID, data, companyID)
      .then(() => {
        Notification('success', __('Product version updated successfully'))
        confirmCb()
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot update product version')
        Notification('error', __('Error occured'), __('We could not update the product version.'))
        setLoading(false)
      })
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (!selectedVersion) {
      Notification('error', __('Error occured'), __('Please select a product version.'))
      return
    }
    setLoading(true)
    updateLicenseProductVersion()
  }

  return (
    <Modal
      confirmCb={handleSubmit}
      closeCb={handleClose}
      disabled={isLoading}
      title={__('Add product version')}
      size='sm'
    >
      <div className='LicenseProductVersionForm'>
        {loadingVersions ? (
          <ContentLoader />
        ) : (
          <div>
            <Label text={__('Product version')} inputId='version' />
            <Selector
              id='version'
              value={selectedVersion}
              options={versions.map(v => ({
                label: `${v.version} ${
                  v.environment
                    ? `(${displayValue(
                        get(
                          environmentOptions.find(o => o.value === get(v, 'environment')),
                          'label'
                        )
                      )})`
                    : ''
                }`,
                value: v.id
              }))}
              handleChange={val => {
                setDirty(true)
                setSelectedVersion(val)
              }}
            />
          </div>
        )}
      </div>
      {isDirtyFormAlertDisplayed && (
        <DirtyFormAlert
          dirty={dirty}
          closeAlert={() => setDirtyFormDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  )
}

LicenseProductVersionForm.propTypes = {
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
  licenseID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  productID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  companyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  productVersionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

export default LicenseProductVersionForm
